import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { FaBrain, FaAtom } from "react-icons/fa"; // add icons
import {
  FaDatabase,
  FaTable,
  FaProjectDiagram,
  FaServer,
  FaCogs,
  FaCloud,
  FaChartBar,
  FaLock,
  FaCode,
  FaLayerGroup,
} from "react-icons/fa";
import { Link } from "react-router-dom";

const SQL = () => {
  useEffect(() => {
    AOS.init({ duration: 1200, once: true });
  }, []);

  const roadmap = [
    {
      step: "Step 1",
      title: "SQL Basics",
      desc: "Introduction to Databases, Tables, and Queries.",
      icon: <FaDatabase />,
    },
    {
      step: "Step 2",
      title: "DDL & DML",
      desc: "Learn CREATE, ALTER, SELECT, INSERT, UPDATE, DELETE.",
      icon: <FaTable />,
    },
    {
      step: "Step 3",
      title: "Joins & Keys",
      desc: "Master INNER, OUTER joins and primary/foreign keys.",
      icon: <FaProjectDiagram />,
    },
    {
      step: "Step 4",
      title: "Transactions",
      desc: "ACID properties, COMMIT, ROLLBACK, SAVEPOINT.",
      icon: <FaLock />,
    },
    {
      step: "Step 5",
      title: "Views & Indexes",
      desc: "Create efficient queries with Views and Indexing.",
      icon: <FaLayerGroup />,
    },
    {
      step: "Step 6",
      title: "Stored Procedures & Triggers",
      desc: "Automate workflows and enforce rules with SQL code blocks.",
      icon: <FaCode />,
    },
    {
      step: "Step 7",
      title: "Optimization",
      desc: "Indexes, query tuning, execution plans.",
      icon: <FaChartBar />,
    },
  ];

  const usageAreas = [
    {
      title: "Web Apps",
      desc: "Powering relational data in backend systems.",
      icon: <FaServer />,
    },
    {
      title: "Cloud Databases",
      desc: "Used in AWS RDS, Azure SQL, Google Cloud SQL.",
      icon: <FaCloud />,
    },
    {
      title: "Data Warehousing",
      desc: "ETL pipelines and big data storage rely on SQL.",
      icon: <FaDatabase />,
    },
    {
      title: "Business Intelligence",
      desc: "SQL drives dashboards and analytics tools.",
      icon: <FaChartBar />,
    },
  ];

  const futureScope = [
    {
      title: "AI + SQL",
      text: "AI-powered SQL assistants for query generation.",
      icon: (
        <FaBrain className="text-cyan-400 text-5xl mx-auto mb-4 animate-bounce" />
      ),
    },
    {
      title: "Serverless SQL",
      text: "Cloud providers offering SQL-as-a-service.",
      icon: (
        <FaServer className="text-green-400 text-5xl mx-auto mb-4 animate-pulse" />
      ),
    },
    {
      title: "Graph & NoSQL Hybrid",
      text: "SQL merging with NoSQL for flexibility.",
      icon: (
        <FaProjectDiagram className="text-yellow-400 text-5xl mx-auto mb-4 animate-spin-slow" />
      ),
    },
    {
      title: "Quantum Databases",
      text: "Future possibility of quantum SQL queries.",
      icon: (
        <FaAtom className="text-pink-400 text-5xl mx-auto mb-4 animate-bounce" />
      ),
    },
  ];

  return (
    <div className="relative text-gray-100 overflow-hidden">
      {/* HEADER */}
      <header
        className=" top-0 z-50 shadow-md py-16 flex flex-col items-center justify-center gap-4"
        style={{
          backgroundImage:
            "url('/assets/photo-1555949963-ff9fe0c870eb.AVIF')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="relative z-10 text-center">
          <h1
            data-aos="zoom-in"
            className="text-4xl md:text-5xl font-bold text-cyan-400 mt-5"
          >
            Master SQL Development
          </h1>
          <p data-aos="fade-up" className="text-gray-200 text-lg mt-3">
            From Basics to Advanced Data Management 🚀
          </p>
          <Link
            to="/contact"
            data-aos="zoom-in"
            data-aos-delay="300"
            className="mt-6 px-6 py-3 bg-cyan-500 text-white rounded-full font-bold shadow-md hover:bg-cyan-600 transition animate-bounce inline-block"
          >
            Enroll Now
          </Link>
        </div>
      </header>

      {/* ROADMAP */}
      <section
        className="relative py-16 px-6"
        style={{
          backgroundImage:
            "url('/assets/photo-1504639725590-34d0984388bd.AVIF')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-white/85"></div>
        <div className="relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-black">
            SQL Learning Roadmap
          </h2>
          <div className="grid gap-8 max-w-5xl mx-auto">
            {roadmap.map((item, i) => (
              <div
                key={i}
                className={`group flex items-start gap-6 bg-gray-100 rounded-xl shadow-lg p-6 hover:shadow-cyan-500/40 transition ${
                  i === 0
                    ? "animate-jump"
                    : i === 1
                    ? "animate-bubble"
                    : i === 2
                    ? "animate-wiggle"
                    : i === 3
                    ? "animate-pulse-scale"
                    : i === 4
                    ? "animate-bounce-in"
                    : i === 5
                    ? "animate-float"
                    : "animate-swing"
                }`}
                data-aos="fade-up"
                data-aos-delay={i * 150}
              >
                <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center text-2xl text-white bg-cyan-500 rounded-full shadow-md">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-cyan-700">
                    {item.step}: {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USAGE AREAS */}
      <section
        className="relative py-16 px-6 bg-[url('https://images.unsplash.com/photo-1518770660439-4636190af475?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')] bg-cover bg-center"
        data-aos="fade-left"
      >
        <div className="absolute inset-0 bg-gray-900/85"></div>
        <div className="relative">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-cyan-300">
            Where SQL is Used
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {usageAreas.map((area, i) => (
              <div
                key={i}
                className="relative w-full h-52 perspective"
                data-aos="flip-left"
                data-aos-delay={i * 150}
              >
                <div className="absolute w-full h-full transition-transform duration-700 transform-style-preserve-3d hover:rotate-y-180">
                  <div className="absolute w-full h-full bg-gray-800 rounded-2xl flex flex-col items-center justify-center p-4 backface-hidden shadow-lg">
                    <div className="text-4xl text-cyan-400 mb-3">
                      {area.icon}
                    </div>
                    <h3 className="text-lg font-bold">{area.title}</h3>
                  </div>
                  <div className="absolute w-full h-full bg-cyan-600 rounded-2xl flex items-center justify-center p-4 rotate-y-180 backface-hidden shadow-lg">
                    <p className="text-center text-white text-sm">
                      {area.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FUTURE SCOPE */}
      <section
        className="relative py-16 px-6 bg-[url('https://images.unsplash.com/photo-1498050108023-c5249f4df085?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')] bg-cover bg-center"
        data-aos="zoom-in"
      >
        <div className="absolute inset-0 bg-black/75"></div>
        <div className="relative max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-cyan-300">
            Future of SQL
          </h2>
          <div className="overflow-x-auto flex space-x-6 pb-4 scrollbar-hide justify-center">
            {futureScope.map((item, i) => (
              <div
                key={i}
                className="min-w-[250px] bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-2xl shadow-md hover:shadow-cyan-500/40 transition text-center"
                data-aos="fade-up"
                data-aos-delay={i * 150}
              >
                {item.icon}
                <h3 className="text-xl font-bold text-cyan-400 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-300">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer
        className="py-8 flex flex-col items-center gap-4 bg-black/80"
        data-aos="fade-up"
      >
        <Link
          to="/contact"
          data-aos="zoom-in"
          data-aos-delay="300"
          className="mt-6 px-6 py-3 bg-cyan-500 text-white rounded-full font-bold shadow-md hover:bg-cyan-600 transition animate-bounce inline-block"
        >
          Enroll Now
        </Link>
      </footer>

      {/* Custom Animations */}
      <style>{`
        .perspective { perspective: 1000px; }
        .backface-hidden { -webkit-backface-visibility: hidden; backface-visibility: hidden; }
        .rotate-y-180 { transform: rotateY(180deg); }

        /* Jumping */
        @keyframes jump {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-jump { animation: jump 1.5s infinite ease-in-out; }

        /* Floating bubbles */
        @keyframes bubble {
          0% { transform: translateY(0) scale(1); opacity: 1; }
          50% { transform: translateY(-8px) scale(1.05); opacity: 0.9; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        .animate-bubble { animation: bubble 2s infinite ease-in-out; }

        /* Wiggle */
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-3deg); }
          75% { transform: rotate(3deg); }
        }
        .animate-wiggle { animation: wiggle 1s infinite ease-in-out; }

        /* Pulse scale */
        @keyframes pulseScale {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .animate-pulse-scale { animation: pulseScale 1.8s infinite ease-in-out; }

        /* Bounce-in */
        @keyframes bounceIn {
          0% { transform: scale(0.8); opacity: 0; }
          50% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); }
        }
        .animate-bounce-in { animation: bounceIn 1s ease-out; }

        /* Float up & down */
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .animate-float { animation: float 2.5s infinite ease-in-out; }

        /* Swing */
        @keyframes swing {
          20% { transform: rotate(15deg); }
          40% { transform: rotate(-10deg); }
          60% { transform: rotate(5deg); }
          80% { transform: rotate(-5deg); }
          100% { transform: rotate(0deg); }
        }
        .animate-swing { animation: swing 2s infinite ease-in-out; }

        /* Custom shake for Enroll button */
        [data-aos="shake"] { animation: shake 1s infinite; }
        @keyframes shake {
          0% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          50% { transform: translateX(5px); }
          75% { transform: translateX(-5px); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default SQL;
